import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useLocation, useNavigate } from 'react-router-dom';
import { showToast } from './utils';

const ProductList = ({ selectedCategory }) => {
  const [products, setProducts] = useState([]);
  const location = useLocation();
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  // Load user info
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/users/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Failed to fetch current user:', res.status, errorText);
          return;
        }
        const data = await res.json();
        setCurrentUser(data);
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };
    fetchCurrentUser();
  }, [token]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      let url = `${API_URL}/api/products`;
      if (location.search) url += location.search;
      if (selectedCategory) url += `?category=${selectedCategory}`;

      try {
        const res = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : {}
          }
        });

        if (!res.ok) {
          const errorText = await res.text();
          console.error('Failed to fetch products:', res.status, errorText);
          showToast("Failed to load products", "error");
          return;
        }

        const data = await res.json();
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
        showToast("Failed to load products", "error");
      }
    };

    fetchProducts();
  }, [location.search, token, selectedCategory]);

  const handleBuy = async (product) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showToast("You are not logged in", "error");
        return;
      }

      const phone = prompt("Enter your phone number") || '';
      const email = prompt("Enter your email") || '';
      const location = prompt("Enter your delivery location") || '';

      if (!phone || !email || !location) {
        showToast("Phone, Email, and Location are required", "error");
        return;
      }

      const res = await fetch(`${API_URL}/api/buy/${product.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: phone, email, location })
      });

      const data = await res.json();
      if (res.ok) {
        showToast("Order placed successfully", "success");
        fetchMyProducts(currentUser?.id);
      } else {
        showToast(data.message || "Error placing order", "error");
      }
    } catch (err) {
      console.error('Error placing order:', err);
      showToast("Failed to place order", "error");
    }
  };

  const handleDelete = async (productId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this product?");
    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        setProducts(products.filter(p => p.id !== productId));
        showToast("Product deleted successfully", "success");
      } else {
        const error = await res.json();
        showToast(error.message || "Failed to delete product", "error");
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      showToast("Network error. Could not delete product.", "error");
    }
  };

  const fetchMyProducts = async (userId) => {
    if (!userId) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/my-products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        // You can update cart count here using Context or state management
      }
    } catch (err) {
      console.error("Failed to fetch cart", err);
    }
  };

  const handleAddToCart = async (product) => {
    try {
      const res = await fetch(`${API_URL}/api/cart/add/${product.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        showToast("Added to cart", "success");
      } else {
        const error = await res.json();
        showToast(error.message || "Could not add to cart", "error");
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      showToast("Network error. Could not add to cart", "error");
    }
  };

  // Scrollable Image Gallery Component
  const ImageGallery = ({ mainImage, extraImages }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const goToPrevious = () => {
      setCurrentIndex(prev => Math.max(0, prev - 1));
    };

    const goToNext = () => {
      setCurrentIndex(prev => Math.min(extraImages.length - 1, prev + 1));
    };

    const currentImage = extraImages[currentIndex]
      ? `${API_URL}/api/uploads/${extraImages[currentIndex]}`
      : `${API_URL}/api/uploads/${mainImage}`;

    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: '180px',
        overflow: 'hidden',
        borderRadius: '8px',
        marginBottom: '10px'
      }}>
        {/* Main image fallback */}
        <img
          src={`${API_URL}/api/uploads/${mainImage}`}
          alt="Main"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: extraImages.length === 0 ? 'block' : 'none'
          }}
        />

        {/* Extra images scroll view */}
        {extraImages.length > 0 && (
          <>
            <img
              src={currentImage}
              alt={`Preview ${currentIndex}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'all 0.3s ease'
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                padding: '10px',
                cursor: 'pointer',
                zIndex: 1
              }}
            >
              ❮
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                padding: '10px',
                cursor: 'pointer',
                zIndex: 1
              }}
            >
              ❯
            </button>
          </>
        )}
      </div>
    );
  };

  if (products.length === 0) {
    return (
      <div style={{ textAlign: 'center', margin: '4rem 0' }}>
        <img src="/images/icons/empty-box.png" alt="No products" width="100" />
        <h3>No products available yet.</h3>
        <p>Check back later or upload one yourself!</p>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '2rem',
      padding: '2rem 1rem'
    }}>
      {products.map(product => (
        <div key={product.id} style={{
          border: '1px solid #ccc',
          borderRadius: '10px',
          padding: '1rem',
          width: '280px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
          textAlign: 'center',
          backgroundColor: '#fff',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.02)' }
        }}>
          {/* Image Gallery */}
          <ImageGallery mainImage={product.image_path} extraImages={product.extra_images || []} />

          <h3 style={{ color: '#2c3e50' }}>{product.name}</h3>
          <p style={{ fontStyle: 'italic', color: '#7f8c8d', minHeight: '60px' }}>
            {product.description}
          </p>
          <p style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            KES {product.price.toFixed(2)}
          </p>

          {/* ✅ Show delete button if admin OR if owner */}
          {(currentUser && (currentUser.is_admin || product.user_id === currentUser.id)) && (
            <button
              onClick={() => handleDelete(product.id)}
              style={{
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '5px',
                marginBottom: '10px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Delete
            </button>
          )}

          {/* Conditional Labels */}
          {currentUser && product.user_id === currentUser.id && !product.is_approved && (
            <p style={{ color: 'orange', fontWeight: 'bold' }}>
              ⚠️ Pending Approval
            </p>
          )}

          {/* Buy Now Button */}
          <button
            onClick={() => handleBuy(product)}
            style={{
              background: '#2ecc71',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '5px',
              cursor: 'pointer',
              marginTop: '10px',
              fontWeight: 'bold'
            }}
          >
            Order Now
          </button>

          {/* Add to Cart Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart(product)
            }}
            style={{
              background: '#f39c12',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '5px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            ➕ Add to Cart
          </button>
        </div>
      ))}
    </div>
  );
};

export default ProductList;
